/**
 * Extracts a user-friendly error message from various error formats
 * Prevents React errors when trying to render objects as children
 *
 * @param error - The error object from API call
 * @param fallbackMessage - Default message if no error message found
 * @returns A string message safe to display in UI
 */
export function extractErrorMessage(
  error: any,
  fallbackMessage: string = "An error occurred"
): string {
  // Try to get backend message from axios error response
  const backendMsg = error?.response?.data?.message;

  if (backendMsg) {
    // Handle array of messages
    if (Array.isArray(backendMsg)) {
      return backendMsg.join(", ");
    }

    // Handle string message
    if (typeof backendMsg === "string") {
      return backendMsg;
    }

    // Handle nested object message (e.g., { message: { message: "...", error: "...", statusCode: 401 } })
    if (typeof backendMsg === "object" && backendMsg !== null) {
      // If it has a nested message property that's a string, use that
      if ("message" in backendMsg && typeof backendMsg.message === "string") {
        return backendMsg.message;
      }
      // If it has an error property that's a string, use that
      if ("error" in backendMsg && typeof backendMsg.error === "string") {
        return backendMsg.error;
      }
      // Otherwise stringify it as last resort
      return JSON.stringify(backendMsg);
    }
  }

  // Fallback to error.message
  if (error?.message && typeof error.message === "string") {
    return error.message;
  }

  // Final fallback
  return fallbackMessage;
}
